import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("role");
    setUser(null);
    navigate("/login");
  }, [navigate]);

  const refreshAccessToken = useCallback(async (refreshToken) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("token", data.token);

        const role = localStorage.getItem("role");
        setUser({ token: data.token, role });
      } else {
        logout();
      }
    } catch (error) {
      logout();
    }
  }, [logout]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");
    const role = localStorage.getItem("role");

    if (token && role) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        const isTokenExpired = decodedToken.exp * 1000 < Date.now();

        if (isTokenExpired && refreshToken) {
          refreshAccessToken(refreshToken);
        } else if (isTokenExpired) {
          logout();
        } else {
          setUser({ token, role });
        }
      } catch (error) {
        logout();
      }
    }
  }, [logout, refreshAccessToken]);

  const login = (token, refreshToken) => {
    const decodedToken = JSON.parse(atob(token.split(".")[1]));

    if (!decodedToken.role) {
      return false;
    }

    const role = decodedToken.role || "member";
    localStorage.setItem("token", token);
    localStorage.setItem("role", role);

    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    }

    setUser({ token, role });

    navigate("/");
    return true;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
